import { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";

import DstButton from "../../components/DstButton/DstButton";
import Lister from "../../components/Lister/Lister";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import TruncatedText from "../../components/TruncatedText/TruncatedText";
import { useSession } from "../../contexts/SessionContext";
import { EphemeralExercise } from "../../models/EphemeralExercise";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { EphemeralExerciseListItem } from "./EphemeralExercises.d";
import { fetchEphemeralExercises } from "./EphemeralExercises.function";

export const EphemeralExercises = () => {
  const { language } = useSession();
  const [searchQuery, setSearchQuery] = useState("");
  const [exercises, setExercises] = useState<EphemeralExercise[]>([]);
  const [onlyActive, setOnlyActive] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchEphemeralExercises(setExercises, setLoaded, language);
  }, [language]);

  const items = useMemo<EphemeralExerciseListItem[]>(
    () =>
      exercises
        .filter((exercise) => !onlyActive || !exercise.is_deprecated)
        .map((exercise) => ({
          ...exercise,
          topic: exercise.topic?.join(", ") || "-",
        }))
        .filter((exercise) =>
          [exercise.ID, exercise.resume, exercise.topic, exercise.succeeded]
            .join(" ")
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        ),
    [exercises, searchQuery, onlyActive]
  );

  const columns = [
    {
      header: translate(language, "PAGES.EPHEMERAL_EXERCISES.ID"),
      key: "ID",
      sortType: "string",
      sortable: true,
      render: (exercise: EphemeralExerciseListItem) => <span className="text-nowrap">{exercise.ID}</span>,
    },
    {
      header: translate(language, "PAGES.EPHEMERAL_EXERCISES.RESUME"),
      key: "resume",
      sortType: "string",
      sortable: true,
      render: (exercise: EphemeralExerciseListItem) => <TruncatedText text={exercise.resume} lines={2} />,
    },
    {
      header: translate(language, "PAGES.EPHEMERAL_EXERCISES.TOPICS"),
      key: "topic",
      sortType: "string",
      sortable: true,
    },
    {
      header: translate(language, "PAGES.EPHEMERAL_EXERCISES.SUCCEEDED"),
      key: "succeeded",
      sortType: "number",
      sortable: true,
    },
    {
      header: translate(language, "PAGES.EPHEMERAL_EXERCISES.ACTIVE"),
      key: "is_deprecated",
      sortable: true,
      render: (exercise: EphemeralExerciseListItem) => (
        <div className="text-center">
          {exercise.is_deprecated ? (
            <span className="dst-icon-x dark-red-font" />
          ) : (
            <span className="dst-icon-check green-font" />
          )}
        </div>
      ),
    },
    {
      header: translate(language, "PAGES.EPHEMERAL_EXERCISES.CREATED_AT"),
      key: "dt_created",
      sortType: "date",
      sortable: true,
      render: (exercise: EphemeralExerciseListItem) => {
        const date = new Date(exercise.dt_created * 1000).toISOString();
        return <span title={formatDateHumanly(language, date, true)}>{formatDateHumanly(language, date)}</span>;
      },
    },
    {
      header: translate(language, "PAGES.EPHEMERAL_EXERCISES.UPDATED_AT"),
      key: "dt_updated",
      sortType: "date",
      sortable: true,
      render: (exercise: EphemeralExerciseListItem) => {
        if (exercise.dt_updated == null) return <span>—</span>;
        const date = new Date(exercise.dt_updated * 1000).toISOString();
        return <span title={formatDateHumanly(language, date, true)}>{formatDateHumanly(language, date)}</span>;
      },
    },
  ];

  return (
    <div className="m-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex flex-wrap gap-3 align-items-center">
          <h2 className="mb-0 me-3">{translate(language, "PAGES.EPHEMERAL_EXERCISES.TITLE")}</h2>
          <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
          <DstButton
            btnClass="h-100 d-flex align-items-center me-2"
            btnImageBefore={
              <Form.Check
                type="switch"
                className="check-box s-bold me-2"
                checked={onlyActive}
                readOnly
                aria-label={translate(language, "PAGES.EPHEMERAL_EXERCISES.ONLY_ACTIVE")}
              />
            }
            value={translate(language, "PAGES.EPHEMERAL_EXERCISES.ONLY_ACTIVE")}
            variant="light"
            btnHeight="50px"
            btnWidth="200px"
            clickFunction={() => setOnlyActive(!onlyActive)}
          />
        </div>
      </div>
      <Lister
        key={`${searchQuery}-${onlyActive}`}
        columns={columns}
        items={items}
        mock={!loaded}
        pagination
        paginationAlwaysShowFooter
        itemsPerPage={50}
      />
    </div>
  );
};

export default EphemeralExercises;
